import React from "react";
import { cn } from "@/lib/utils";

/**
 * Heading — token-driven typographic scale.
 *
 * Replaces ad-hoc `text-3xl` / `text-4xl` / `text-5xl` inline classes with
 * a scale that maps to `--ds-font-*` tokens so future scale tweaks stay
 * global. Levels 1-6 map to semantic <h1>-<h6>; the visual size can be
 * overridden with `size` (independent of semantic level).
 *
 * Sizes:
 *   xs    → 12px    (nano labels, deprecated — prefer `<span>` for these)
 *   sm    → 14px
 *   md    → 16px
 *   lg    → 20px
 *   xl    → 24px
 *   2xl   → 28px    (matches PageHeader.title)
 *   3xl   → 36px
 *   4xl   → 48px    (marketing display, use sparingly)
 *
 * a11y contract: always renders a real <hX> tag; use `size` to detach
 * visual scale from semantic level (never skip h1 → h3 visually).
 */

const SIZE_CLASS = {
  xs: "text-[12px] leading-tight",
  sm: "text-[14px] leading-snug",
  md: "text-[16px] leading-snug",
  lg: "text-[20px] leading-tight",
  xl: "text-[24px] leading-tight",
  "2xl": "text-[28px] leading-tight",
  "3xl": "text-[36px] leading-tight",
  "4xl": "text-[48px] leading-none",
};

const WEIGHT_CLASS = {
  regular: "font-normal",
  medium: "font-medium",
  semibold: "font-semibold",
  bold: "font-bold",
};

export function Heading({
  level = 1,
  size,
  weight = "semibold",
  as,
  className,
  children,
  "data-testid": testId,
  ...rest
}) {
  const Tag = as || `h${Math.max(1, Math.min(6, Number(level) || 1))}`;
  // Default visual size derived from semantic level when `size` is omitted.
  const derivedSize = size || ({ 1: "2xl", 2: "xl", 3: "lg", 4: "md", 5: "sm", 6: "xs" }[level] || "md");
  return (
    <Tag
      data-testid={testId}
      className={cn(
        SIZE_CLASS[derivedSize] || SIZE_CLASS.md,
        WEIGHT_CLASS[weight] || WEIGHT_CLASS.semibold,
        "text-[var(--ds-color-ink)]",
        "font-[var(--ds-font-display)]",
        className,
      )}
      style={{ letterSpacing: derivedSize === "2xl" || derivedSize === "3xl" || derivedSize === "4xl" ? "-0.02em" : undefined }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

export default Heading;
